import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../../../axiosinterceptor';
import { toast } from 'react-toastify';


const getUserId = () => sessionStorage.getItem("userId");
const normalizeDate = (d) => d ? d.split(' ')[0] : null;

// Initial State
const initialState = {
  //Users On Leave Today
  usersOnLeaveToday: {
    data: [],
    pagination: { page: 1, limit: 10, total_records: 0, total_pages: 0 },
    loading: false,
    error: null
  },

  // Separate coder lists for each section
  codersLeaveAllocation: {
    data: [],
    page: 1,
    hasMore: true,
    loading: false,
    error: null
  },

  codersWorkAssignment: {
    data: [],
    page: 1,
    hasMore: true,
    loading: false,
    error: null
  },

  codersWeeklyAvailability: {
    data: [],
    page: 1,
    hasMore: true,
    loading: false,
    error: null
  },

  //User Leave Allocation
  leaveAllocation: {
    loading: false,
    error: null,
    success: false
  },

  //User Leave Updation
  leaveUpdation: {
    loading: false,
    error: null,
    success: false
  },

  //User Leave Cancel
  leaveCancel: {
    loading: false,
    error: null,
    success: false
  },

  //Users on Leave (All)
  // allLeaves: {
  //   data: [],
  //   active: [],
  //   inactive: [],
  //   pagination: { page: 1, limit: 10, total_records: 0, total_pages: 0 },
  //   loading: false,
  //   error: null
  // },

  activeLeaves: {
    data: [],
    pagination: { page: 1, limit: 10, total_records: 0, total_pages: 0 },
    loading: false,
    error: null
  },

  inactiveLeaves: {
    data: [],
    pagination: { page: 1, limit: 10, total_records: 0, total_pages: 0 },
    loading: false,
    error: null
  },


  //User Work Assignment
  workAssignment: {
    loading: false,
    error: null,
    success: false
  },

  //User Work Assigned List
  workAssignedList: {
    data: [],
    pagination: { page: 1, limit: 10, total_records: 0, total_pages: 0 },
    loading: false,
    error: null
  },

  // Form States
  leaveAllocationForm: {
    user_id: [],
    username: [],
    leave_from: '',
    leave_to: '',
    created_by: getUserId(),
    updated_by: getUserId()
  },

  // UI States
  selectedLeaveUsers: [],
  selectedWorkUsers: [],
  selectedWorkDays: [],
  showLeaveModal: false,

  // Search States
  search: {
    workAssignedSearch: '',
    leaveCoderSearch: '',
    workCoderSearch: '',
    weeklySearch: ''
  },

  editingLeaves: {},

  alreadyOnLeaveCoders: [],
};

//THUNKS

// Fetch Users On Leave Today
export const fetchUsersOnLeaveToday = createAsyncThunk(
  'availability/fetchUsersOnLeaveToday',
  async ({ page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      
      const response = await axiosInstance.get('/UsersOnLeaveToday', { params });

      
      const data = response.data.body?.data || response.data.data || [];
      const pagination = response.data.body?.pagination || response.data.pagination || {
        page, limit, total_records: data.length, total_pages: 1
      };
      
      return { data, pagination };
    } catch (error) {
      console.error('Fetch users on leave error:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Allocate Leave
export const allocateLeave = createAsyncThunk(
  'availability/allocateLeave',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/UserLeaveAllocation', {
        ...payload,
        leave_from: normalizeDate(payload.leave_from),
        leave_to: normalizeDate(payload.leave_to),
        created_by: getUserId(),
        updated_by: getUserId()
      });
      
      return response.data;
    } catch (error) {
      console.error('Allocate leave error:', error);
      
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update Leave
export const updateLeave = createAsyncThunk(
  'availability/updateLeave',
  async ({ user_leave_id, user_id, username, leave_from, leave_to, updated_by }, { rejectWithValue }) => {
    try {
      const payload = {
        user_leave_id,
        user_id,
        username,
        leave_from: normalizeDate(leave_from),
        leave_to: normalizeDate(leave_to),
        updated_by: updated_by || getUserId()
      };
      
      console.log('Updating leave with payload:', payload);
      
      const response = await axiosInstance.put('/UserLeaveUpdation', payload);
      
      return response.data;
    } catch (error) {
      console.error('Update leave error:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Cancel Leave
export const cancelLeave = createAsyncThunk(
  'availability/cancelLeave',
  async ({ user_leave_id, user_id, username, leave_from, leave_to }, { rejectWithValue }) => {
    try {
      const payload = {
        user_leave_id,
        user_id,
        username,
        leave_from: normalizeDate(leave_from),
        leave_to: normalizeDate(leave_to)
      };
      
      console.log('Cancelling leave with payload:', payload);
      
      const response = await axiosInstance.delete('/UserLeaveCancel', { 
        data: payload 
      });
      
      return response.data;
    } catch (error) {
      console.error('Cancel leave error:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

//Fetch active leaves
export const fetchActiveLeaves = createAsyncThunk(
  'availability/fetchActiveLeaves',
  async ({ page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;

      const response = await axiosInstance.get(
        '/UsersOnLeave',
        { params }
      );

      return {
        data: response.data.body?.data || response.data.data || [],
        pagination:
          response.data.body?.pagination || response.data.pagination
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

//Fetch Leaves History
export const fetchInactiveLeaves = createAsyncThunk(
  'availability/fetchInactiveLeaves',
  async ({ page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;

      const response = await axiosInstance.get(
        '/UsersOnLeavePast',
        { params }
      );

      return {
        data: response.data.body?.data || response.data.data || [],
        pagination:
          response.data.body?.pagination || response.data.pagination
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);



// Assign Work Days
export const assignWorkDays = createAsyncThunk(
  'availability/assignWorkDays',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/UserWorkAssignmentAllocationUpdation', 
        {
          ...payload,
          created_by: getUserId(),
          updated_by: getUserId()
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Assign work days error:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch Work Assigned List
export const fetchWorkAssignedList = createAsyncThunk(
  'availability/fetchWorkAssignedList',
  async ({ page = 1, limit = 10, search = '' } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;
      
      const response = await axiosInstance.get(
        search ? '/UsersWorkAssignedSearch' : '/UsersWorkAssigned',
        { params }
      );
      
      const data = response.data.data || [];
      const pagination = response.data.pagination || {
        page, limit, total_records: data.length, total_pages: 1
      };
      
      return { data, pagination };
    } catch (error) {
      console.error('Fetch work assigned list error:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch Coders for Leave Allocation
export const fetchCodersForLeaveAllocation = createAsyncThunk(
  'availability/fetchCodersForLeaveAllocation',
  async ({ page = 1, limit = 10, search = '' } = {}, { rejectWithValue, getState }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;
      
      console.log('Fetching coders for leave allocation, page:', page, 'search:', search);
      
      const response = await axiosInstance.get(
        search ? '/CodersSearch' : '/CodersList',
        { params }
      );
      
      const data = response.data.data || [];
      const pagination = response.data.pagination || {
        page, limit, total_records: data.length, total_pages: 1
      };
      
      return { 
        data, 
        pagination, 
        page,
        search,
        append: page > 1
      };
    } catch (error) {
      console.error('Fetch coders for leave error:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch Coders for Work Assignment
export const fetchCodersForWorkAssignment = createAsyncThunk(
  'availability/fetchCodersForWorkAssignment',
  async ({ page = 1, limit = 10, search = '' } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;
      
      console.log('Fetching coders for work assignment, page:', page, 'search:', search);
      
      const response = await axiosInstance.get(
        search ? '/CodersSearch' : '/CodersList',
        { params }
      );
      
      const data = response.data.data || [];
      const pagination = response.data.pagination || {
        page, limit, total_records: data.length, total_pages: 1
      };
      
      return { 
        data, 
        pagination, 
        page,
        search,
        append: page > 1
      };
    } catch (error) {
      console.error('Fetch coders for work error:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch Coders for Weekly Availability
export const fetchCodersForWeeklyAvailability = createAsyncThunk(
  'availability/fetchCodersForWeeklyAvailability',
  async ({ page = 1, limit = 10, search = '' } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (search) params.search = search;

      const response = await axiosInstance.get(
        search ? '/UsersWorkAssignedSearch' : '/UsersWorkAssigned',
        { params }
      );

      const raw = response.data.data || [];

      const normalized = raw.map((u) => ({
        user_id: u.user_id,
        username: u.username,
        work_days: typeof u.work_days === 'string'
          ? u.work_days.replace(/[{}]/g, '').split(',').filter(Boolean)
          : []
      }));

      return {
        data: normalized,
        pagination: response.data.pagination,
        page,         
        search  
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);



//SLICER

const userAvailabilitySlicer = createSlice({
  name: 'availability',
  initialState,
  reducers: {
    // Form Actions
    updateSelectedLeaveUsers: (state, action) => {
      state.selectedLeaveUsers = action.payload;
    },
    updateSelectedWorkUsers: (state, action) => {
      state.selectedWorkUsers = action.payload;
    },
    updateSelectedWorkDays: (state, action) => {
      state.selectedWorkDays = action.payload;
    },
    
    // Search Actions
    updateSearch: (state, action) => {
      state.search = {
        ...state.search,
        ...action.payload
      };
    },

    
    // UI Actions
    setShowLeaveModal: (state, action) => {
      state.showLeaveModal = action.payload;
      if (!action.payload) {
        state.editingLeaves = {};
      }
    },
    
    // Update editing state for a specific leave
    updateEditingLeave: (state, action) => {
      const { leaveId, field, value } = action.payload;
      if (!state.editingLeaves[leaveId]) {
        state.editingLeaves[leaveId] = {};
      }
      state.editingLeaves[leaveId][field] = value;
    },
    
    // Clear editing state
    clearEditingLeave: (state, action) => {
      const { leaveId } = action.payload;
      delete state.editingLeaves[leaveId];
    },
    
    // Reset coder lists when search changes
    resetCodersOnSearch: (state, action) => {
      const { section, search } = action.payload;
      if (section === 'leaveAllocation' && search !== state.search.coderSearch) {
        state.codersLeaveAllocation.data = [];
        state.codersLeaveAllocation.page = 1;
        state.codersLeaveAllocation.hasMore = true;
      }
      if (section === 'workAssignment' && search !== state.search.coderSearch) {
        state.codersWorkAssignment.data = [];
        state.codersWorkAssignment.page = 1;
        state.codersWorkAssignment.hasMore = true;
      }
      if (section === 'weeklyAvailability' && search !== state.search.weeklySearch) {
        state.codersWeeklyAvailability.data = [];
        state.codersWeeklyAvailability.page = 1;
        state.codersWeeklyAvailability.hasMore = true;
      }
    },
    
    // Reset Actions
    resetLeaveAllocation: (state) => {
      state.selectedLeaveUsers = [];
      state.leaveAllocation = initialState.leaveAllocation;
    },
    resetWorkAssignment: (state) => {
      state.selectedWorkUsers = [];
      state.selectedWorkDays = [];
      state.workAssignment = initialState.workAssignment;
    },
    clearErrors: (state) => {
      state.leaveAllocation.error = null;
      state.leaveUpdation.error = null;
      state.leaveCancel.error = null;
      state.workAssignment.error = null;
    },

    //reser existing leave users

    clearAlreadyOnLeaveCoders: (state) => {
      state.alreadyOnLeaveCoders = [];
    },
  },
  extraReducers: (builder) => {
    //USERS ON LEAVE TODAY
    builder
      .addCase(fetchUsersOnLeaveToday.pending, (state) => {
        state.usersOnLeaveToday.loading = true;
        state.usersOnLeaveToday.error = null;
      })
      .addCase(fetchUsersOnLeaveToday.fulfilled, (state, action) => {
        state.usersOnLeaveToday.loading = false;
        state.usersOnLeaveToday.data = action.payload.data;
        state.usersOnLeaveToday.pagination = action.payload.pagination;
      })
      .addCase(fetchUsersOnLeaveToday.rejected, (state, action) => {
        state.usersOnLeaveToday.loading = false;
        state.usersOnLeaveToday.error = action.payload;
      });

    //ALLOCATE LEAVE
    builder
      .addCase(allocateLeave.pending, (state) => {
        state.leaveAllocation.loading = true;
        state.leaveAllocation.error = null;
        state.leaveAllocation.success = false;
      })
      
      .addCase(allocateLeave.fulfilled, (state, action) => {
        state.leaveAllocation.loading = false;
        state.leaveAllocation.success = true;

        // Clear users on leave success
        state.selectedLeaveUsers = [];
        state.alreadyOnLeaveCoders = [];
        
        // toast.success(
        //   action.payload?.message || 'Leave allocated successfully'
        // );
      })
      .addCase(allocateLeave.rejected, (state, action) => {
      state.leaveAllocation.loading = false;
      state.leaveAllocation.error = action.payload;
    
      const errorData = action.payload;
    
      if (errorData && typeof errorData === 'object' &&
          errorData.message && errorData.message.includes('existing') &&
          Array.isArray(errorData.blocked_users)) {
      
        const existingLeaves = errorData.blocked_users || [];
      
        if (existingLeaves.length > 0) {
          const existingIds = existingLeaves.map(user => user.user_id);
          state.selectedLeaveUsers = state.selectedLeaveUsers.filter(
            user => !existingIds.includes(user.user_id)
          );
        
          state.alreadyOnLeaveCoders = existingLeaves.map(user => ({
            user_id: user.user_id,
            username: user.username
          }));
        
          // Show WARNING toast (not error) for existing leaves
          // toast.warning(errorData.message || 'Some leaves already exist');
        }
      } else {
        // const errorMessage = typeof errorData === 'string' ? errorData :
        //                     (errorData?.message || 'Failed to allocate leave');
        // toast.error(errorMessage);
      }
    })

    //UPDATE LEAVE
    builder
      .addCase(updateLeave.pending, (state) => {
        state.leaveUpdation.loading = true;
        state.leaveUpdation.error = null;
        state.leaveUpdation.success = false;
      })
      .addCase(updateLeave.fulfilled, (state, action) => {
        state.leaveUpdation.loading = false;
        state.leaveUpdation.success = true;
        if (action.meta.arg.user_leave_id) {
          delete state.editingLeaves[action.meta.arg.user_leave_id];
        }

        // toast.success(action.payload?.message || 'Leave Updated Successfully')
      })
      .addCase(updateLeave.rejected, (state, action) => {
        state.leaveUpdation.loading = false;
        state.leaveUpdation.error = action.payload;

        // toast.error(action.payload || 'Failed to update Leave')        
      });

    //CANCEL LEAVE
    builder
      .addCase(cancelLeave.pending, (state) => {
        state.leaveCancel.loading = true;
        state.leaveCancel.error = null;
        state.leaveCancel.success = false;
      })
      .addCase(cancelLeave.fulfilled, (state,action) => {
        state.leaveCancel.loading = false;
        state.leaveCancel.success = true;

        // toast.success(
        //   action.payload?.message || 'Leave cancelled successfully'
        // );
      })
      .addCase(cancelLeave.rejected, (state, action) => {
        state.leaveCancel.loading = false;
        state.leaveCancel.error = action.payload;

        // toast.error(action.payload || 'Failed to cancel leave');
      });

    //ALL ACTIVE LEAVES

    builder
    .addCase(fetchActiveLeaves.pending, (state) => {
      state.activeLeaves.loading = true;
    })
    .addCase(fetchActiveLeaves.fulfilled, (state, action) => {
      state.activeLeaves.loading = false;
      state.activeLeaves.data = action.payload.data;
      state.activeLeaves.pagination = action.payload.pagination;
    })
    .addCase(fetchActiveLeaves.rejected, (state, action) => {
      state.activeLeaves.loading = false;
      state.activeLeaves.error = action.payload;
    });

    //ALL INACTIVE LEAVES

    builder
      .addCase(fetchInactiveLeaves.pending, (state) => {
        state.inactiveLeaves.loading = true;
      })
      .addCase(fetchInactiveLeaves.fulfilled, (state, action) => {
        state.inactiveLeaves.loading = false;
        state.inactiveLeaves.data = action.payload.data;
        state.inactiveLeaves.pagination = action.payload.pagination;
      })
      .addCase(fetchInactiveLeaves.rejected, (state, action) => {
        state.inactiveLeaves.loading = false;
        state.inactiveLeaves.error = action.payload;
      });


    //ASSIGN WORK DAYS
    builder
      .addCase(assignWorkDays.pending, (state) => {
        state.workAssignment.loading = true;
        state.workAssignment.error = null;
        state.workAssignment.success = false;
      })
      .addCase(assignWorkDays.fulfilled, (state,action) => {
        state.workAssignment.loading = false;
        state.workAssignment.success = true;
        state.selectedUsers = [];
        state.selectedWorkDays = [];

        // toast.success(
        //   action.payload?.message || 'Work days assigned successfully'
        // );
      })
      .addCase(assignWorkDays.rejected, (state, action) => {
        state.workAssignment.loading = false;
        state.workAssignment.error = action.payload;

        // toast.error(action.payload || 'Failed to assign work days');
      });

    //WORK ASSIGNED LIST
    builder
      .addCase(fetchWorkAssignedList.pending, (state) => {
        state.workAssignedList.loading = true;
        state.workAssignedList.error = null;
      })
      .addCase(fetchWorkAssignedList.fulfilled, (state, action) => {
        state.workAssignedList.loading = false;
        state.workAssignedList.data = action.payload.data;
        state.workAssignedList.pagination = action.payload.pagination;
      })
      .addCase(fetchWorkAssignedList.rejected, (state, action) => {
        state.workAssignedList.loading = false;
        state.workAssignedList.error = action.payload;
      });

    //CODERS FOR LEAVE ALLOCATION
    builder
      .addCase(fetchCodersForLeaveAllocation.pending, (state, action) => {
        state.codersLeaveAllocation.loading = true;
        state.codersLeaveAllocation.error = null;

        if (action.meta.arg?.page === 1) {
          state.codersLeaveAllocation.data = [];
        }
      })
      .addCase(fetchCodersForLeaveAllocation.fulfilled, (state, action) => {
        state.codersLeaveAllocation.loading = false;

        if (action.payload.page === 1) {
          state.codersLeaveAllocation.data = action.payload.data;
          state.codersLeaveAllocation.page = 1;
        } else {
          state.codersLeaveAllocation.data = [
            ...state.codersLeaveAllocation.data,
            ...action.payload.data
          ];
          state.codersLeaveAllocation.page = action.payload.page;
        }

        const totalPages = action.payload.pagination.total_pages || 1;
        state.codersLeaveAllocation.hasMore =
          action.payload.page < totalPages;
      })


      .addCase(fetchCodersForLeaveAllocation.rejected, (state, action) => {
        state.codersLeaveAllocation.loading = false;
        state.codersLeaveAllocation.error = action.payload;
      });

    //CODERS FOR WORK ASSIGNMENT
    builder
      .addCase(fetchCodersForWorkAssignment.pending, (state, action) => {
        state.codersWorkAssignment.loading = true;
        state.codersWorkAssignment.error = null;

        if (action.meta.arg?.page === 1) {
          state.codersWorkAssignment.data = [];
        }
      })
      .addCase(fetchCodersForWorkAssignment.fulfilled, (state, action) => {
        state.codersWorkAssignment.loading = false;

        if (action.payload.page === 1) {
          state.codersWorkAssignment.data = action.payload.data;
          state.codersWorkAssignment.page = 1;
        } else {
          state.codersWorkAssignment.data = [
            ...state.codersWorkAssignment.data,
            ...action.payload.data
          ];
          state.codersWorkAssignment.page = action.payload.page;
        }

        const totalPages = action.payload.pagination.total_pages || 1;
        state.codersWorkAssignment.hasMore =
          action.payload.page < totalPages;
      })


      .addCase(fetchCodersForWorkAssignment.rejected, (state, action) => {
        state.codersWorkAssignment.loading = false;
        state.codersWorkAssignment.error = action.payload;
      });

    //CODERS FOR WEEKLY AVAILABILITY 
    builder
      .addCase(fetchCodersForWeeklyAvailability.pending, (state, action) => {
        state.codersWeeklyAvailability.loading = true;
        state.codersWeeklyAvailability.error = null;

        // Clear data for search / fresh load
        if (action.meta.arg?.page === 1) {
          state.codersWeeklyAvailability.data = [];
        }
      })
      .addCase(fetchCodersForWeeklyAvailability.fulfilled, (state, action) => {
        state.codersWeeklyAvailability.loading = false;

        if (action.payload.page === 1) {
          // Always replace on page 1
          state.codersWeeklyAvailability.data = action.payload.data;
          state.codersWeeklyAvailability.page = 1;
        } else {
          // Append on pagination
          state.codersWeeklyAvailability.data = [
            ...state.codersWeeklyAvailability.data,
            ...action.payload.data
          ];
          state.codersWeeklyAvailability.page = action.payload.page;
        }

        const totalPages = action.payload.pagination?.total_pages || 1;
        state.codersWeeklyAvailability.hasMore =
          action.payload.page < totalPages;
      })

      .addCase(fetchCodersForWeeklyAvailability.rejected, (state, action) => {
        state.codersWeeklyAvailability.loading = false;
        state.codersWeeklyAvailability.error = action.payload;
      });
  }
});

// Export Actions
export const {
  updateSelectedLeaveUsers,
  updateSelectedWorkUsers,
  updateSelectedWorkDays,
  updateSearch,
  setShowLeaveModal,
  updateEditingLeave,
  clearEditingLeave,
  resetCodersOnSearch,
  resetLeaveAllocation,
  resetWorkAssignment,
  clearErrors,
  clearAlreadyOnLeaveCoders
} = userAvailabilitySlicer.actions;

// Export Reducer
export default userAvailabilitySlicer.reducer;

